/* Small pieces shared by the photo-report grid and its review modal. */

import React from "react";
import { Tag } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";

import { PHOTO_REPORT } from "../../config/lang";
import { translate } from "../../util/util";

/* Lives in util.js now — the visits list needs it too. Re-exported so this module's callers
   keep their import. */
export { fill } from "../../util/util";

/** `approval.is_approved` is true / false / null — null means still on verification. */
export const statusOf = item => (item?.approval?.is_approved === true ? "approved" : item?.approval?.is_approved === false ? "rejected" : "pending");

export const fullName = user => (user ? [user.first_name, user.last_name].filter(Boolean).join(" ") : "");

export const initialsOf = name =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map(word => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/* Tint + `-700` label + an icon: colour is never the only signal (spec/01). */
const PILL = {
  approved: { tone: "ok", Icon: CheckCircleOutlined, key: "status_approved" },
  rejected: { tone: "no", Icon: CloseCircleOutlined, key: "status_rejected" },
  pending: { tone: "wait", Icon: ClockCircleOutlined, key: "status_pending" },
};

/**
 * The design's status pill: antd `<Tag>`, per spec/02 ("Status pill → `<Tag>` with `-bg` fill and
 * `-700` label").
 *
 * It carries a `pr-pill--*` class rather than antd's `color="success" | "error" | "warning"`,
 * and that is not a shortcut avoided out of habit: antd derives a colour Tag's text from
 * `colorSuccess` / `colorWarning`, which are our `-500` FILL tokens — `--success-500` on
 * `--success-bg` measures 3.70:1 and fails AA (spec/01#status-colour). The tint/label pair in
 * `photo-review.scss` is the only combination that passes.
 */
export function StatusPill({ status, lang, inline }) {
  const { tone, Icon, key } = PILL[status] || PILL.pending;
  return (
    <Tag variant="filled" icon={<Icon />} className={`status-tag status-tag--${tone} pr-pill${inline ? " pr-pill--inline" : ""}`}>
      {translate(PHOTO_REPORT, key, lang)}
    </Tag>
  );
}
